// React

// Components
import { gql, useLazyQuery, useMutation, useQuery } from "@apollo/client";
import { Card, Grid, Icon, Tooltip, Collapse, Select, MenuItem } from "@mui/material";
import * as XLSX from "xlsx";

// Data

// import { UserContext } from "context/user";
import CartelError from "components/Carteles/Error";
import Loading from "components/Carteles/Loading";
import SinDatos from "components/Carteles/SinDatos";
import GeneralLayout from "components/LayoutContainers/GeneralLayout";
import CustomPagination from "components/Pagination";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
import SoftTypography from "components/SoftTypography";
import DataTable from "components/Tables/DataTable";
import { MessageManager } from "context";
import { ConfirmarContext } from "context/ConfirmarContext";
import { MenuContext } from "context/menuContext";
import { useCallback, useContext, useEffect, useState } from "react";
import { getUproColor } from "utils/colors";
import dataClientes from "./data/dataClientes";
import ModalCuentaCorriente from "./ModalCuentaCorriente";
import ModalPedidos from "./ModalPedidos";
import ModalReporte from "./ModalReporte";
import ModalUsuariosVip from "./ModalUsuariosVip";
import ModalCuentaCorrienteNoFuncionario from "./ModalCuentaCorrienteNoFuncionario";
import { UserContext } from "context/user";
import ModalAddCuenta from "layouts/clientes/ModalCuentaCorrienteNoFuncionario/modalAdd";
import ModalCargaMasiva from "./ModalCargaMasiva";
import dayjs from "dayjs";
import ModalReporteVouchers from "./ModalReporteVouchers";

const isClienteAlumnoAcademica = (cliente = {}) =>
  String(cliente?.appUsuario || "").trim() === "Alumnos" &&
  String(cliente?.tipoUsuario || "").trim() === "Alumno";

function Clientes() {
  const [search, setSearch] = useState("");
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState(null);
  const [modalPedidos, setModalPedidos] = useState(false);
  const [modalCuentaCorriente, setModalCuentaCorriente] = useState(false);
  const [openModalCargarSaldos, setOpenModalCargarSaldos] = useState(false);
  const [rows, setRows] = useState([]);
  const { confirmar } = useContext(ConfirmarContext);
  const { handleSnackbar } = useContext(MessageManager);
  const [filter, setFilter] = useState({
    appUsuario: "Compras",
  });
  const { user, verificarAcceso } = useContext(UserContext);
  const [openModalAdd, setOpenModalAdd] = useState(false);
  const [tipo, setTipo] = useState(null);
  const [openModalCargaMasiva, setOpenModalCargaMasiva] = useState(false);
  const [tipoMasivo, setTipoMasivo] = useState(null);

  const [pagesInfo, setPagesInfo] = useState({
    pages: 0,
    total: 0,
  });
  const [orderBy, setOrderBy] = useState({
    campo: "apellido",
    orden: "ASC",
  });
  const { data, loading, error, refetch } = useQuery(
    gql`
      query getClientes(
        $pagination: PaginationInput
        $order: Order
        $search: String
        $filter: JSON
      ) {
        paginationInfo {
          pages
          total
        }
        clientes(pagination: $pagination, order: $order, search: $search, filter: $filter) {
          id
          nombre
          apellido
          correo
          dni
          cuentaUpro
          appUsuario
          esBecado
          habilitadoCuentaCorriente
        }
      }
    `,
    {
      variables: {
        filter: filter,
        search: search || null,
        pagination: { page: page, limit: 10 },
        order: { field: orderBy.campo, order: orderBy.orden },
      },
      fetchPolicy: "no-cache",
      cachePolicy: "no-cache",
    }
  );

  // Query para obtener historial de cargas de saldo para Excel
  const [getHistorialExcel, { loading: loadingExcel }] = useLazyQuery(
    gql`
      query getHistorialCargaSaldosExcel {
        historialCargaSaldosExcel {
          fechaPago
          tipoCarga
          idCliente
          nombre
          apellido
          dni
          tipoUsuario
          appUsuario
          totalPago
        }
      }
    `,
    {
      fetchPolicy: "no-cache",
      onCompleted: (data) => {
        if (data?.historialCargaSaldosExcel) {
          exportToExcel(data.historialCargaSaldosExcel);
        }
      },
      onError: (error) => {
        handleSnackbar(error?.message || "Error al obtener los datos", "error");
      },
    }
  );

  const handleFilter = (e) => {
    const { name, value } = e.target;
    if (value === 1) {
      setFilter(() => ({
        appUsuario: "Compras",
        cuentaUpro: false,
        _pagos: { estado: "Pendiente" },
      }));
    } else {
      setFilter(() => ({
        appUsuario: "Compras",
        [name]: value === "0" ? undefined : value,
      }));
    }
  };

  //Save clientes
  const [saveCliente] = useMutation(
    gql`
      mutation saveCliente($input: ClienteInput!) {
        saveCliente(input: $input) {
          id
        }
      }
    `,
    {
      fetchPolicy: "no-cache",
      cachePolicy: "no-cache",
      onCompleted: (data) => {
        handleSnackbar("Cliente actualizado", "success");
        refetch();
      },
      onError: (error) => {
        handleSnackbar(error?.message || "Error al guardar el cliente", "error");
      },
    }
  );

  const { menu } = useContext(MenuContext);

  const { columns, getRows } = dataClientes;

  const handleOrderBy = (campo) => {
    if (orderBy.campo === campo) {
      if (orderBy.orden === "ASC") {
        setOrderBy({
          campo: campo,
          orden: "DESC",
        });
      } else {
        setOrderBy({
          campo: campo,
          orden: "ASC",
        });
      }
    } else {
      setOrderBy({
        campo: campo,
        orden: "ASC",
      });
    }
  };

  const handleOpenModalAdd = (tipo) => {
    setTipo(tipo);
    setOpenModalAdd(true);
  };

  const handleCloseModalAdd = () => {
    setOpenModalAdd(false);
    setTipo(null);
  };

  // Función para generar el Excel
  const generateExcel = () => {
    getHistorialExcel();
  };

  // Función para exportar datos a Excel
  const exportToExcel = (data) => {
    if (!data || data.length === 0) {
      handleSnackbar("No hay datos para exportar", "warning");
      return;
    }

    // Formatear datos para Excel
    const excelData = data.map((item) => ({
      "Fecha de Pago": dayjs(item.fechaPago).format("DD/MM/YYYY"),
      "Tipo de Carga": item.tipoCarga,
      "ID Cliente": item.idCliente,
      "Nombre": item.nombre,
      "Apellido": item.apellido,
      "DNI": item.dni,
      "Tipo de Usuario": item.tipoUsuario,
      "Aplicación": item.appUsuario,
      "Total Pago": item.totalPago,
    }));

    // Crear hoja de cálculo
    const ws = XLSX.utils.json_to_sheet(excelData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Historial");

    // Generar nombre de archivo con fecha actual
    const fileName = `Historial_Carga_Saldos_${dayjs().format("YYYY-MM-DD_HHmmss")}.xlsx`;

    // Descargar archivo
    XLSX.writeFile(wb, fileName);

    handleSnackbar("Excel descargado exitosamente", "success");
  };

  const handleOpenMenu = useCallback((e, item) => {
    menu({
      open: e.target,
      align: "right",
      options: [
        {
          name: "Ver pedidos",
          onClick: () => {
            setSelected(item);
            setModalPedidos(true);
          },
          disabled: false,
          icon: { icon: "receipt", color: "primary" },
        },
        ...(item.habilitadoCuentaCorriente
          ? [
            {
              name: "Cuenta corriente",
              onClick: () => {
                setSelected(item);
                setModalCuentaCorriente(true);
              },
              disabled: false,
              icon: { icon: "account_balance_wallet", color: getUproColor("uproGreen") },
            }
          ] : []),
        // ...(item.habilitadoCuentaCorriente && !item.esBecado
        //   ? [
        //     {
        //       name: "Cargar saldo a cuenta corriente",
        //       onClick: () => {
        //         setSelected(item);
        //         handleOpenModalAdd("CargarSaldo");
        //       },
        //       disabled: false,
        //       icon: { icon: "account_balance_wallet", color: getUproColor("uproGreen") },
        //     }
        //   ] : []),
        // ...(!item.esBecado
        //   ? [
        //     {
        //       name: "Becar cliente",
        //       onClick: () => {
        //         setSelected(item);
        //         handleOpenModalAdd("BecarCliente");
        //       },
        //       disabled: false,
        //       icon: { icon: "account_balance_wallet", color: getUproColor("uproGreen") },
        //     }
        //   ] : []),
        ...(user?.sede?.id == 1 &&
          (!isClienteAlumnoAcademica(item) || item.habilitadoCuentaCorriente) ? [{
          name: `${item.habilitadoCuentaCorriente ? "Deshabilitar" : "Habilitar"} cuenta corriente`,
          icon: {
            icon: "account_balance",
            color: item.habilitadoCuentaCorriente ? "warning" : getUproColor("uproGreen"),
          },
          onClick: () => {
            confirmar({
              title: `${item.habilitadoCuentaCorriente ? "Deshabilitar" : "Habilitar"
                } cuenta corriente`,
              message: `¿Está seguro que desea ${item.habilitadoCuentaCorriente ? "deshabilitar" : "habilitar"
                } la cuenta corriente de ${item.nombre} ${item.apellido}?`,
              func: () => {
                saveCliente({
                  variables: {
                    input: {
                      id: item.id,
                      habilitadoCuentaCorriente: !item.habilitadoCuentaCorriente,
                    },
                  },
                });
              },
            });
          },
        }] : []),
        ...(user?.sede?.id == 1 ? [{
          // name: "Marcar  cuenta de funcionario",
          name: `${item.cuentaUpro ? "Habilitar" : "Deshabilitar"} cuenta de funcionario`,
          onClick: () => {
            confirmar({
              title: `${item.cuentaUpro ? "Habilitar" : "Deshabilitar"} cuenta de funcionario`,
              message: `¿Está seguro que desea ${item.cuentaUpro ? "habilitar" : "deshabilitar"
                } a ${item.nombre} ${item.apellido} como cuenta de funcionario?`,
              icon: "verified_user",
              func: () => {
                saveCliente({
                  variables: {
                    input: {
                      id: item.id,
                      cuentaUpro: !item.cuentaUpro,
                    },
                  },
                });
              },
            });
          },
          icon: { icon: "verified_user", color: getUproColor("uproBlue") },
        }] : []),
      ],
    });
  }, [menu, setSelected, setModalPedidos, setModalCuentaCorriente, user, confirmar, saveCliente]);

  const handleSearch = (event) => {
    const newTimeout = setTimeout(() => {
      setSearch(event.target.value);
      setPage(1);
    }, 500);
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }
    setSearchTimeout(newTimeout);
  };

  useEffect(() => {
    setRows(getRows(data?.clientes, handleOpenMenu));
    setPagesInfo(data?.paginationInfo);
  }, [data]);
  const [openModalUsuariosVip, setOpenModalUsuariosVip] = useState(false);
  const handleOpenModalUsuariosVip = () => {
    setOpenModalUsuariosVip(true);
  };
  const handleCloseModalUsuariosVip = () => {
    setOpenModalUsuariosVip(false);
  };

  const [openModalReporte, setOpenModalReporte] = useState(false);
  const handleOpenModalReporte = () => {
    setOpenModalReporte(true);
  };
  const handleCloseModalReporte = () => {
    setOpenModalReporte(false);
  };
  const [openModalReporteVouchers, setOpenModalReporteVouchers] = useState(false);
  const handleOpenModalReporteVouchers = () => {
    setOpenModalReporteVouchers(true);
  };
  const handleCloseModalReporteVouchers = () => {
    setOpenModalReporteVouchers(false);
  };

  return (
    <GeneralLayout>
      <Card>
        <Grid container spacing={2} p={3}>
          <Grid item xs={12} justifyContent="space-between" display="flex">
            <SoftBox>
              <SoftTypography variant="h6">Clientes</SoftTypography>
            </SoftBox>
          </Grid>
          <Grid xs={12} item>
            <SoftBox
              display="flex"
              alignItems="center"
              justifyContent="flex-end"
              mt={1}
              gap={2}
              flexWrap="wrap"
              sx={{
                flexDirection: { xs: "column", sm: "row" },
                "& > *": {
                  margin: 0,
                },
              }}
            >
              <SoftBox
                sx={{
                  width: { xs: "100%", sm: "auto" },
                  minWidth: 200,
                }}
              >
                <Select
                  name="appUsuario"
                  onChange={handleFilter}
                  value={filter?.appUsuario || "0"}
                  fullWidth
                  size="small"
                >
                  <MenuItem value="0">
                    <SoftBox display="flex" alignItems="center" gap={1}>
                      <Icon fontSize="small">apps</Icon>
                      Todas las aplicaciones
                    </SoftBox>
                  </MenuItem>
                  <MenuItem value="Compras">
                    <SoftBox display="flex" alignItems="center" gap={1}>
                      <Icon fontSize="small">shopping_cart</Icon>
                      Upro Gestión
                    </SoftBox>
                  </MenuItem>
                  <MenuItem value="Alumnos">
                    <SoftBox display="flex" alignItems="center" gap={1}>
                      <Icon fontSize="small">school</Icon>
                      Upro Alumnos
                    </SoftBox>
                  </MenuItem>
                  <MenuItem value="Comedor">
                    <SoftBox display="flex" alignItems="center" gap={1}>
                      <Icon fontSize="small">restaurant</Icon>
                      Upro Comedor
                    </SoftBox>
                  </MenuItem>
                </Select>
              </SoftBox>

              <SoftBox
                sx={{
                  width: { xs: "100%", sm: "auto" },
                  minWidth: 200,
                }}
              >
                <Select
                  name="cuentaUpro"
                  onChange={handleFilter}
                  value={filter?._pagos?.estado === "Pendiente" ? 1 : filter?.cuentaUpro ?? "0"}
                  fullWidth
                  size="small"
                >
                  <MenuItem value="0">
                    <SoftBox display="flex" alignItems="center" gap={1}>
                      <Icon fontSize="small">account_balance</Icon>
                      Todos los clientes
                    </SoftBox>
                  </MenuItem>
                  <MenuItem value={true}>
                    <SoftBox display="flex" alignItems="center" gap={1}>
                      <Icon fontSize="small">account_balance</Icon>
                      Cuenta Upro
                    </SoftBox>
                  </MenuItem>
                  <MenuItem value={false}>
                    <SoftBox display="flex" alignItems="center" gap={1}>
                      <Icon fontSize="small">account_balance</Icon>
                      Cuenta de funcionario (Todos)
                    </SoftBox>
                  </MenuItem>
                  <MenuItem value={1}>
                    <SoftBox display="flex" alignItems="center" gap={1}>
                      <Icon fontSize="small">account_balance</Icon>
                      Cuenta de funcionario (Pagos pendientes)
                    </SoftBox>
                  </MenuItem>
                </Select>
              </SoftBox>

              <SoftBox
                sx={{
                  width: { xs: "100%", sm: "auto" },
                  minWidth: 200,
                  "& .MuiInputBase-root": {
                    height: "39px",
                    minHeight: "39px",
                    boxSizing: "border-box",
                  },
                  "& input": {
                    paddingTop: "10.5px",
                    paddingBottom: "10.5px",
                  },
                }}
              >
                <SoftInput
                  placeholder="Escribe aquí..."
                  icon={{ component: "search", direction: "left" }}
                  onChange={handleSearch}
                  fullWidth
                  size="small"
                />
              </SoftBox>

              {verificarAcceso(59) && <SoftBox
                sx={{
                  width: { xs: "100%", sm: "auto" },
                  // display: { xs: "block", md: "none" },
                }}
              >
                <Tooltip title="Cargar saldos" placement="top">
                  <SoftButton
                    color="secondary"
                    circular
                    iconOnly
                    onClick={(e) => {
                      menu({
                        open: e.target,
                        align: "right",
                        options: [
                          // {
                          //   name: "Cargar saldo a cuenta corriente",
                          //   onClick: () => {
                          //     setTipoMasivo("CargarSaldo");
                          //     setOpenModalCargaMasiva(true);
                          //   },
                          //   disabled: false,
                          //   icon: { icon: "add_card", color: getUproColor("uproYellow") },
                          // },
                          // {
                          //   name: "Becar cliente",
                          //   onClick: () => {
                          //     setTipoMasivo("BecarCliente");
                          //     setOpenModalCargaMasiva(true);
                          //   },
                          //   disabled: false,
                          //   icon: { icon: "payments", color: getUproColor("uproGreen") },
                          // },
                          {
                            name: "Descargar historial en Excel",
                            onClick: () => { generateExcel() },
                            disabled: false,
                            icon: { icon: "backup_table", color: getUproColor("uproPink") },
                          }
                        ]
                      })
                    }}
                    fullWidth
                  >
                    <Icon>account_balance</Icon>
                  </SoftButton>
                </Tooltip>
              </SoftBox>}

              <SoftBox
                sx={{
                  width: { xs: "100%", sm: "auto" },
                  // display: { xs: "block", md: "none" },
                }}
              >
                <Tooltip title="Configuracion de usuarios" placement="top">
                  <SoftButton
                    color="primary"
                    circular
                    iconOnly
                    onClick={handleOpenModalUsuariosVip}
                    fullWidth
                  >
                    <Icon>manage_accounts</Icon>
                  </SoftButton>
                </Tooltip>
              </SoftBox>

              {/* <SoftBox
                sx={{
                  width: { sm: "auto" },
                  display: { xs: "none", md: "block" },
                }}
              >
                <Tooltip title="Configuracion de usuarios" placement="top">
                  <SoftButton
                    color="primary"
                    circular
                    onClick={handleOpenModalUsuariosVip}
                    fullWidth
                  >
                    <Icon>manage_accounts</Icon>
                    &nbsp;Configuracion usuario
                  </SoftButton>
                </Tooltip>
              </SoftBox> */}

              <SoftBox
                sx={{
                  width: { xs: "100%", sm: "auto" },
                  // display: { xs: "block", md: "none" },
                }}
              >
                <Tooltip title="Reporte de cuentas corrientes" placement="top">
                  <SoftButton
                    color="uproBlue"
                    circular
                    iconOnly
                    onClick={handleOpenModalReporte}
                    fullWidth
                  >
                    <Icon>summarize</Icon>
                  </SoftButton>
                </Tooltip>
              </SoftBox>

              <SoftBox
                sx={{
                  width: { xs: "100%", sm: "auto" },
                  // display: { xs: "block", md: "none" },
                }}
              >
                <Tooltip title="Reporte Vouchers" placement="top">
                  <SoftButton
                    color="uproGreen"
                    circular
                    iconOnly
                    onClick={handleOpenModalReporteVouchers}
                    fullWidth
                  >
                    <Icon>assignment</Icon>
                  </SoftButton>
                </Tooltip>
              </SoftBox>

            </SoftBox>
          </Grid>
        </Grid>
        <SoftBox>
          {loading ? (
            <Loading />
          ) : error ? (
            <CartelError />
          ) : rows?.length > 0 ? (
            <DataTable columns={columns} rows={rows} order={orderBy} setOrder={handleOrderBy} />
          ) : (
            <SinDatos />
          )}
        </SoftBox>
        <CustomPagination
          loading={loading}
          length={data?.clientes?.length}
          total={pagesInfo?.total}
          pages={pagesInfo?.pages}
          page={page}
          setPage={setPage}
          noCard
        />
        <ModalPedidos
          open={modalPedidos}
          handleClose={() => {
            setSelected(null);
            setModalPedidos(false);
          }}
          selected={selected}
        />
        <ModalReporteVouchers
          open={openModalReporteVouchers}
          handleClose={handleCloseModalReporteVouchers}
        />
        <ModalCuentaCorriente
          open={modalCuentaCorriente}
          handleClose={() => {
            setSelected(null);
            setModalCuentaCorriente(false);
          }}
          selected={selected}
        />
        <ModalAddCuenta
          open={openModalAdd}
          tipo={tipo}
          handleClose={handleCloseModalAdd}
          refetch={refetch}
          cliente={selected}
        />
        {/* <ModalCuentaCorrienteNoFuncionario open={openModalCuentaCorrienteNoFuncionario} handleClose={() => setOpenModalCuentaCorrienteNoFuncionario(false)} selected={selected} /> */}
        <ModalUsuariosVip open={openModalUsuariosVip} handleClose={handleCloseModalUsuariosVip} />
        <ModalReporte open={openModalReporte} handleClose={handleCloseModalReporte} />
        <ModalCargaMasiva
          open={openModalCargaMasiva}
          handleClose={() => {
            setOpenModalCargaMasiva(false);
            setTipoMasivo(null);
          }}
          tipo={tipoMasivo}
          idSede={user?.sede?.id ?? null}
        />
      </Card>
    </GeneralLayout>
  );
}

export default Clientes;
